import { useEffect, useRef, useState } from "react";
import { Bell, Check, AlertTriangle, Ban, ShieldCheck, Info } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  warning: { icon: AlertTriangle, color: "text-yellow-400" },
  ban: { icon: Ban, color: "text-red-400" },
  ban_lifted: { icon: ShieldCheck, color: "text-emerald-400" },
  appeal_accepted: { icon: ShieldCheck, color: "text-emerald-400" },
  appeal_rejected: { icon: Ban, color: "text-red-400" },
  appeal_info_requested: { icon: Info, color: "text-cyan-400" },
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => api.get("/notifications").then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => { const n = !open; setOpen(n); if (n && unread > 0) load(); };
  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };
  const markOne = async (id) => { await api.post(`/notifications/${id}/read`).catch(() => {}); load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle}
        className="relative p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notif-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-[#fff] text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl z-[70]">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-900">
            <span className="font-head font-semibold text-sm">Notifications</span>
            {unread > 0 && (
              <button data-testid="notif-mark-all" onClick={markAll}
                className="text-xs text-volt hover:text-volt-hover flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="divide-y divide-zinc-800/70">
            {items.length === 0 && <p data-testid="notif-empty" className="text-zinc-600 text-sm px-4 py-8 text-center">You're all caught up. 🎉</p>}
            {items.map((n) => {
              const meta = ICONS[n.type] || { icon: Info, color: "text-zinc-400" };
              const Icon = meta.icon;
              return (
                <button key={n.id} data-testid="notif-item" onClick={() => !n.read && markOne(n.id)}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-zinc-800/40 transition-colors ${n.read ? "opacity-60" : ""}`}>
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <span className="truncate">{n.title}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-volt shrink-0" />}
                    </div>
                    <p className="text-xs text-zinc-400 mt-0.5 whitespace-pre-line line-clamp-4">{n.message}</p>
                    <span className="text-[10px] text-zinc-600">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
